import React from 'react';
import { 
  Typography, 
  Box,
  styled,
  TextField
} from '@mui/material';
import { convertNumberToIndianWords } from '../../../../utils/numberToWords';

// Utility function to format prices consistently
const formatPrice = (price: number): string => {
  return price.toLocaleString('en-IN', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
};

interface PriceBreakdownProps {
  pricing: any;
  tripDays: number;
  calculatedPrice?: number; // Make calculatedPrice optional
  negotiationPrice?: number;
  offerInfo?: any;
  distanceDetails?: {
    kmPrice: number;
    dayPrice: number;
    finalPrice: number;
    baseKmLimit: number;
    extraKm: number;
    baseKmPrice: number;
    extraKmPrice: number;
    returnCharges?: number;
  };
  calculatedDistance?: number;
  isRoundTrip?: boolean; // Whether this is a round trip
  returnCharges?: number; // Return charges if not a round trip
}

const PriceBreakdown: React.FC<PriceBreakdownProps> = ({ 
  pricing, 
  tripDays, 
  calculatedPrice, 
  negotiationPrice, 
  offerInfo,
  distanceDetails,
  calculatedDistance,
  isRoundTrip = true, // Default to round trip
  returnCharges = 0 // Default to no return charges
}) => {
  // State for custom negotiation price
  const [customNegotiationPrice, setCustomNegotiationPrice] = React.useState<number | null>(null);
  
  // Calculate Return Charges - only apply them when Round Trip is false
  // Get return charges from distanceDetails if available, otherwise use the passed returnCharges
  const returnChargeValue = distanceDetails?.returnCharges !== undefined ? distanceDetails.returnCharges : returnCharges;
  const actualReturnCharges = !isRoundTrip ? returnChargeValue : 0;
  // Calculate return charges based on round trip status
  // Calculate totals - include return charges when not a round trip
  const baseTotal = pricing.basePrice * tripDays + (pricing.driverAllowance || 0) * tripDays + actualReturnCharges;

  
  // Calculate extra kilometers for negotiation total
  const dailyKmLimit = (() => {
    if (distanceDetails && distanceDetails.baseKmLimit) {
      return Math.round(distanceDetails.baseKmLimit / tripDays);
    }
    return 300; // Default value
  })();
  const totalKmLimit = dailyKmLimit * tripDays;
  const actualDistance = calculatedDistance ? Math.round(calculatedDistance) : 0;
  const extraKm = Math.max(0, actualDistance - totalKmLimit);
  const negotiationPricePerKm = pricing.ratePerKm || pricing.extraKmRate || 12; // Use actual Rate/KM values
  const extraKmPrice = extraKm * negotiationPricePerKm;
  
  // Include extra kilometer price and return charges in negotiation total
  // Use custom negotiation price if provided, otherwise use the default
  const negotiationBasePrice = customNegotiationPrice !== null ? customNegotiationPrice : (pricing.negotiationPrice || (pricing.offerInfo ? pricing.offerInfo.discountedPrice : pricing.basePrice));
  const returnCharge = !isRoundTrip && actualReturnCharges > 0 ? actualReturnCharges : 0;
  const negotiationTotal = negotiationBasePrice * tripDays + (pricing.driverAllowance || 0) * tripDays + extraKmPrice + returnCharge;
  
  // Calculate savings - ensure it's positive by comparing base price to negotiation base price
  const savings = (pricing.basePrice - negotiationBasePrice) * tripDays;
  
  // Use calculatedPrice safely (default to baseTotal if undefined)
  const displayPrice = calculatedPrice !== undefined ? calculatedPrice : baseTotal;
  
  // Default daily kilometer limit
  const defaultDailyLimit = 300; // 300 KM per day
  
  return (
    <Box key={pricing.carTypeId} sx={{ mb: 3, pb: 2, borderRadius: 2, overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
      {/* Car Type and Model */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, backgroundColor: 'rgba(0,0,0,0.2)', p: 1.5, borderTopLeftRadius: 2, borderTopRightRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: (() => {
                // Assign different colors based on car type
                const carType = pricing.carType.toLowerCase();
                if (carType.includes('sedan')) return '#42a5f5'; // Blue for Sedan
                if (carType.includes('suv')) return '#ffb74d'; // Orange for SUV
                if (carType.includes('premium')) return '#4caf50'; // Green for Premium
                if (carType.includes('luxury')) return '#ffd54f'; // Yellow for Luxury
                if (carType.includes('hatchback')) return '#4db6ac'; // Teal for Hatchback
                return '#e0e0e0'; // Default light gray
              })(),
              backgroundColor: (() => {
                // Assign different background colors based on car type
                const carType = pricing.carType.toLowerCase();
                if (carType.includes('sedan')) return 'rgba(66, 165, 245, 0.15)'; // Light blue bg
                if (carType.includes('suv')) return 'rgba(255, 183, 77, 0.15)'; // Light orange bg
                if (carType.includes('premium')) return 'rgba(186, 104, 200, 0.15)'; // Light purple bg
                if (carType.includes('luxury')) return 'rgba(255, 213, 79, 0.15)'; // Light yellow bg
                if (carType.includes('hatchback')) return 'rgba(77, 182, 172, 0.15)'; // Light teal bg
                return 'transparent'; // Default transparent
              })(),
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: 'inline-block'
            }}
          >
            {pricing.carType}
          </Typography>
          <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
            - {pricing.makeCompany} {pricing.model}
          </Typography>
        </Box>
        
        {pricing.offerInfo && (
          <Box sx={{
            backgroundColor: 'rgba(255,152,0,0.15)',
            color: '#ff9800',
            fontWeight: 'bold',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {pricing.offerInfo.name}
          </Box>
        )}
      </Box>
      
      {/* Kilometer Details Section moved outside of car component */}
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, px: 1.5 }}>
        {/* Display Price and Offer Price Calculation */}
        <Box sx={{ flex: 1, minWidth: '200px', backgroundColor: 'rgba(255,255,255,0.03)', p: 1.5, borderRadius: 1.5, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.03)' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0', mb: 0.5 }}>
            Price Calculation:
          </Typography>
          
          {/* Removed Distance-Based Calculation section */}
          
          {/* Kilometer Information Section - Now shown in common header */}
          
          {/* Price Calculation Section */}
          <Box sx={{ mb: 1, backgroundColor: 'rgba(76,175,80,0.05)', p: 1, borderRadius: 1, border: '1px dashed rgba(76,175,80,0.2)' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 0.5 }}>
              Price Details: {!isRoundTrip ? '(Non-Round Trip)' : '(Round Trip)'}
            </Typography>
            
            {/* Display Price */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
                 Price:
              </Typography>
              <Typography variant="body2" sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
                ₹{formatPrice(pricing.basePrice)} x {tripDays} = ₹{formatPrice(pricing.basePrice * tripDays)}
              </Typography>
            </Box>
            
            {/* Driver allowance */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
                Driver Allowance:
              </Typography>
              <Typography variant="body2" sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>
                ₹{formatPrice(pricing.driverAllowance || 0)} x {tripDays} = ₹{formatPrice((pricing.driverAllowance || 0) * tripDays)}
              </Typography>
            </Box>
            
            {/* Return Charges - Always show with highlighting */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, backgroundColor: !isRoundTrip ? 'rgba(255,152,0,0.15)' : 'transparent', p: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: !isRoundTrip ? '#ff9800' : '#e0e0e0', fontSize: '0.85rem', fontWeight: !isRoundTrip ? 'bold' : 'normal' }}>
                Return Charges: {!isRoundTrip ? '(Non-Round Trip)' : '(Round Trip)'}
              </Typography>
              <Typography variant="body2" sx={{ color: !isRoundTrip ? '#ff9800' : '#e0e0e0', fontSize: '0.85rem', fontWeight: !isRoundTrip ? 'bold' : 'normal' }}>
                ₹{formatPrice(actualReturnCharges)}
              </Typography>
            </Box>
            
            {/* Extra Kilometer Price */}
            {(() => {
              // Calculate extra kilometers
              const dailyLimit = (() => {
                if (distanceDetails && distanceDetails.baseKmLimit) {
                  return Math.round(distanceDetails.baseKmLimit / tripDays);
                }
                return 300; // Default value
              })();
              const totalLimit = dailyLimit * tripDays;
              const actualDistance = calculatedDistance ? Math.round(calculatedDistance) : 0;
              const extraKm = actualDistance - totalLimit;
              
              // Always show the extra kilometer price
              // Get price per km from pricing data (Rate/KM)
              const pricePerKm = pricing.ratePerKm || pricing.extraKmRate || 12;
              const extraKmPrice = Math.max(0, extraKm) * pricePerKm; // Ensure we don't use negative extraKm
              
              // Calculate total estimated price
              const basePrice = pricing.basePrice * tripDays;
              const driverAllowance = (pricing.driverAllowance || 0) * tripDays;
              // Always include return charges when not a round trip, regardless of value
              const returnCharge = !isRoundTrip ? actualReturnCharges : 0;
              const totalEstimatedPrice = basePrice + driverAllowance + extraKmPrice + returnCharge;
              
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#ff9800', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      Extra Kilometer Price ({Math.max(0, extraKm)} KM × ₹{pricePerKm}):
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ff9800', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      ₹{formatPrice(extraKmPrice)}
                    </Typography>
                  </Box>
                  
                  {/* Return Charges if not a round trip */}
                  {!isRoundTrip && actualReturnCharges > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#ff9800', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        Return Charges:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ff9800', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        ₹{formatPrice(actualReturnCharges)}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Total Estimated Price */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, mt: 0.5, borderTop: '1px dashed rgba(76,175,80,0.3)' }}>
                    <Typography variant="body2" sx={{ color: '#4caf50', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      Total Estimated Price:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4caf50', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      ₹{formatPrice(totalEstimatedPrice)}
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
          
          {pricing.offerInfo && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              mb: 0.5, 
              mt: 1.5, 
              pt: 1.5, 
              pb: 1, 
              px: 1.5,
              backgroundColor: 'rgba(76, 175, 80, 0.08)',
              borderRadius: '0 0 8px 8px',
              border: '1px dashed rgba(76, 175, 80, 0.3)',
              borderTop: 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  backgroundColor: '#4caf50', 
                  color: '#fff', 
                  fontSize: '0.65rem', 
                  fontWeight: 'bold',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  mr: 1
                }}>
                  SPECIAL OFFER
                </Box>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                  {pricing.offerInfo.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  Discounted Price:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#e0e0e0', textDecoration: 'line-through', fontSize: '0.75rem' }}>
                    ₹{pricing.basePrice.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.95rem' }}>
                    ₹{pricing.offerInfo.discountedPrice.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px dashed #444' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0' }}>
              Total Price:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0' }}>
              {(() => {
                // Format with Indian number system (commas after thousands, then after every two digits)
                const formattedPrice = baseTotal.toLocaleString('en-IN', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2
                });
                
                return (
                  <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0' }}>
                        ₹{formattedPrice}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9e9e9e', textAlign: 'right' }}>
                        {convertNumberToIndianWords(baseTotal)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Typography>
          </Box>
        </Box>
        
        {/* Minimum Price Calculation */}
        <Box sx={{ 
          flex: 1, 
          minWidth: '200px', 
          backgroundColor: 'rgba(76,175,80,0.08)', 
          p: 1.5, 
          borderRadius: 1.5, 
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)', 
          border: '1px solid rgba(76,175,80,0.2)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 'bold', 
              color: '#4caf50', 
              fontSize: '1rem',
              textShadow: '0 1px 1px rgba(0,0,0,0.1)'
            }}>
              Minimum Price Calculation:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                placeholder="Custom price"
                value={customNegotiationPrice !== null ? customNegotiationPrice : ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value === '' ? null : Number(e.target.value);
                  // Prevent negative values
                  if (value !== null && value < 0) return;
                  setCustomNegotiationPrice(value);
                }}
                InputProps={{
                  startAdornment: <span style={{ color: '#ff9800', marginRight: '4px', fontWeight: 'bold' }}>₹</span>,
                  inputProps: { min: 0 } // Set minimum value to 0
                }}
                sx={{
                  width: '120px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,152,0,0.1)',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: 'rgba(255,152,0,0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,152,0,0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#ff9800',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#fff',
                    fontSize: '0.85rem',
                    padding: '8px 10px',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Box>
          </Box>
          
          {/* Kilometer Information Section - Now shown in common header */}
          
          {/* Price Calculation Section */}
          <Box sx={{ 
            mb: 1, 
            backgroundColor: 'rgba(76,175,80,0.1)', 
            p: 1.5, 
            borderRadius: 1, 
            border: '1px solid rgba(76,175,80,0.3)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 'bold', 
              color: '#4caf50', 
              mb: 1,
              fontSize: '0.9rem',
              borderBottom: '1px dashed rgba(76,175,80,0.3)',
              paddingBottom: '4px'
            }}>
              Price Details: {!isRoundTrip ? 
                <span style={{ color: '#ff9800' }}>(Non-Round Trip)</span> : 
                <span>(Round Trip)</span>
              }
            </Typography>
            
            {/* Minimum Price */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8, backgroundColor: 'rgba(255,255,255,0.03)', p: 0.8, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {pricing.offerInfo ? 'Discounted Price:' : 'Minimum Price:'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ff9800', fontSize: '0.9rem', fontWeight: 'bold' }}>
                {/* Use custom negotiation price if provided, otherwise use the default */}
                ₹{formatPrice(customNegotiationPrice !== null ? customNegotiationPrice : (pricing.negotiationPrice || (pricing.offerInfo ? pricing.offerInfo.discountedPrice : pricing.basePrice)))} × {tripDays} = 
                ₹{formatPrice((customNegotiationPrice !== null ? customNegotiationPrice : (pricing.negotiationPrice || (pricing.offerInfo ? pricing.offerInfo.discountedPrice : pricing.basePrice))) * tripDays)}
              </Typography>
            </Box>
            
            {/* Driver allowance */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8, backgroundColor: 'rgba(255,255,255,0.03)', p: 0.8, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 'medium' }}>
                Driver Allowance:
              </Typography>
              <Typography variant="body2" sx={{ color: '#4caf50', fontSize: '0.9rem', fontWeight: 'bold' }}>
                ₹{formatPrice(pricing.driverAllowance || 0)} × {tripDays} = ₹{formatPrice((pricing.driverAllowance || 0) * tripDays)}
              </Typography>
            </Box>
            
            {/* Return Charges - Always show with highlighting */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, backgroundColor: !isRoundTrip ? 'rgba(255,152,0,0.15)' : 'transparent', p: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: !isRoundTrip ? '#ff9800' : '#e0e0e0', fontSize: '0.85rem', fontWeight: !isRoundTrip ? 'bold' : 'normal' }}>
                Return Charges: {!isRoundTrip ? '(Non-Round Trip)' : '(Round Trip)'}
              </Typography>
              <Typography variant="body2" sx={{ color: !isRoundTrip ? '#ff9800' : '#e0e0e0', fontSize: '0.85rem', fontWeight: !isRoundTrip ? 'bold' : 'normal' }}>
                ₹{formatPrice(actualReturnCharges)}
              </Typography>
            </Box>
            
            {/* Extra Kilometer Price */}
            {(() => {
              // Calculate extra kilometers
              const dailyLimit = (() => {
                if (distanceDetails && distanceDetails.baseKmLimit) {
                  return Math.round(distanceDetails.baseKmLimit / tripDays);
                }
                return 300; // Default value
              })();
              const totalLimit = dailyLimit * tripDays;
              const actualDistance = calculatedDistance ? Math.round(calculatedDistance) : 0;
              const extraKm = actualDistance - totalLimit;
              
              // Always show the extra kilometer price
              // Get price per km from pricing data (Rate/KM)
              const pricePerKm = pricing.ratePerKm || pricing.extraKmRate || 12;
              const extraKmPrice = Math.max(0, extraKm) * pricePerKm; // Ensure we don't use negative extraKm
              
              // Calculate total estimated price
              const basePrice = (pricing.negotiationPrice || (pricing.offerInfo ? pricing.offerInfo.discountedPrice : pricing.basePrice)) * tripDays;
              const driverAllowance = (pricing.driverAllowance || 0) * tripDays;
              // Always include return charges when not a round trip, regardless of value
              const returnCharge = !isRoundTrip ? actualReturnCharges : 0;
              const totalEstimatedPrice = basePrice + driverAllowance + extraKmPrice + returnCharge;
              
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#ba68c8', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      Extra Kilometer Price ({Math.max(0, extraKm)} KM × ₹{pricePerKm.toFixed(2)}):
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ba68c8', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      ₹{formatPrice(extraKmPrice)}
                    </Typography>
                  </Box>
                  
                  {/* Return Charges if not a round trip */}
                  {!isRoundTrip && actualReturnCharges > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#ba68c8', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        Return Charges:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ba68c8', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        ₹{formatPrice(actualReturnCharges)}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Total Estimated Price */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, mt: 0.5, borderTop: '1px dashed rgba(76,175,80,0.3)' }}>
                    <Typography variant="body2" sx={{ color: '#4caf50', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      Total Estimated Minimum Price:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4caf50', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      ₹{formatPrice(totalEstimatedPrice)}
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
          
          {/* Total Minimum Price display */}
          {negotiationTotal > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 1.5, 
              pt: 1.5, 
              pb: 0.5,
              borderTop: '1px solid rgba(255,152,0,0.5)',
              backgroundColor: 'rgba(255,152,0,0.1)',
              borderRadius: '0 0 4px 4px',
              px: 1.5
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#ff9800', 
                fontSize: '1rem',
                textShadow: '0 1px 1px rgba(0,0,0,0.1)'
              }}>
                Total Minimum Price:
              </Typography>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#ff9800',
                fontSize: '1rem',
                textShadow: '0 1px 1px rgba(0,0,0,0.1)'
              }}>
                {(() => {
                  // Format with Indian number system (commas after thousands, then after every two digits)
                  const formattedPrice = negotiationTotal.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  });
                  
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: '#ff9800', 
                          fontSize: '1.1rem'
                        }}>
                          ₹{formattedPrice}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ff9800', textAlign: 'right', fontStyle: 'italic' }}>
                          {convertNumberToIndianWords ? convertNumberToIndianWords(negotiationTotal) : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Removed Daily Kilometer Limit Summary - Now shown in common header */}
      
      {/* Savings */}
      <Box sx={{ px: 1.5, mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0' }}>
            Customer Savings:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
            {(() => {
              // Format with Indian number system (commas after thousands, then after every two digits)
              const formattedSavings = savings.toLocaleString('en-IN', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
              });
              const savingsPercent = Math.round((savings / baseTotal) * 100);
              
              return (
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                      ₹{formattedSavings} ({savingsPercent}% off)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#a5d6a7', textAlign: 'right' }}>
                      {convertNumberToIndianWords(savings)}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceBreakdown;
